import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import html2canvas from 'html2canvas';
import { jsPDF } from "jspdf";
import moment from 'moment';
import { BehaviorSubject, Subscription } from 'rxjs';
import font from '../../../assets/fonts/font';
import { TranslatorService } from '../../helpers/getTranslationsHelper/translator.service';
import { LanguageService } from '../../language.service';
import { PdfExporterService } from './pdf-exporter.service';

// Usage in components: 
// htmlElement Input is a text value of the element id you have defined in the template
// Everything that is inside the element with the id you have put in the htmlElement input will be rendered inside the pdf.

// <div class="btn btn-group" >
//   <app-pdf-exporter [htmlElement]="'upper-row'" [button] = "{ styleClass: 'mat-icon-button',color: 'grey',icon: 'picture_as_pdf'}"> 
//   </app-pdf-exporter>
// </div>
export interface PdfExporterBtn {
  styleClass: string;
  color: string;
  icon: string;
  title: string;
  paletteColor?: string;
}

@Component({
  selector: 'app-pdf-exporter',
  templateUrl: './pdf-exporter.component.html',
  styleUrls: ['./pdf-exporter.component.scss']
})
export class PdfExporterComponent implements OnInit, OnDestroy {

  private titleTranslated: string | null = null;
  private filename: string | null = null;
  private translation$: Subscription;
  private _htmlElement: string | null = null;
  private _htmlElement$: BehaviorSubject<boolean> = new BehaviorSubject(false)

  public loading: boolean = false;
  public htmlEl$ = this._htmlElement$.asObservable();
  public _title: string | null = null;
  public value: number = 0;
  private _showIframe: boolean = false;
  private _saveDialog: boolean = false;
  private _openTab: boolean = false;
  private _years: string[] | null = null;
  private _datesRange: string | null = null;
  private _proceedWithPDF: boolean | null = null;

  /**
   * Sets input <string> 
   * @description  HTML Element ID selector with DOM properties
   * @description the id of the wrapper element (the dynamic content of the pdf) defined in the template where the button will appear
   * <div id="pdf-data-to-show">
   *    <div>
   *       <li>1</li>
   *       <li>2</li>
   *    </div>
   * </div>
     <div class="btn btn-group">
      <app-pdf-exporter [htmlElement]="'pdf-data-to-show'" 
         [button]="{ styleClass: 'mat-icon-button',color: 'grey',icon: 'picture_as_pdf'}">
      </app-pdf-exporter>
     </div>
   */
  @Input()
  set htmlElement(htmlElement: string | null) {
    this._htmlElement = htmlElement;
    htmlElement != null ? this._htmlElement$.next(true) : this._htmlElement$.next(false)
  }

  @Input('title')
  set title(title: string) {
    this._title = title
    this._title != null ? this.getTranslation(this._title) : () => { }
  }

  @Input() button: PdfExporterBtn = {
    styleClass: '',
    color: '',
    icon: 'picture_as_pdf',
    title: '',
    paletteColor: ''
  }

  @Input() set showIframe(showIframe: boolean) {
    this._showIframe = showIframe;
  }

  @Input() set saveDialog(saveDialog: boolean) {
    // this._openTab = saveDialog ? false : true;
    this._saveDialog = saveDialog
  }

  @Input() set openTab(openTab: boolean) {
    this._openTab = openTab
  }

  @Input() set years(years: string[] | null) {
    this._years = years
  }

  @Input() set datesRange(datesRange: string | null) {
    this._datesRange = datesRange
  }

  @Input() set proceedWithPDF(proceed: boolean | null) {
    this._proceedWithPDF = proceed;
    this.cdr.detectChanges()
    this.clickedPDFExporterButton.emit(proceed)
    if (proceed !== undefined) {
      this.generatePDF();
    }
  }

  @Output('clickedPDFExporterButton') clickedPDFExporterButton = new EventEmitter<boolean>()

  constructor(
    private pdfExporterService: PdfExporterService,
    private cdr: ChangeDetectorRef,
    private languageService: LanguageService,
    private translatorService: TranslatorService) { }

  ngOnInit() { }

  ngOnDestroy() {
    if (this.translation$) this.translation$.unsubscribe()
  }


  getTranslation(title?) {
    let checkIfTitle = title == null ? this._title : title
    this.translatorService.getToasterTranslation([checkIfTitle])
      .subscribe(res => this.titleTranslated = res[checkIfTitle])
  }

  checkIfTranslationExists(): boolean {
    return this.titleTranslated !== null
  }

  generatePDF() {
    this.loading = true;
    let element = document.getElementById(this._htmlElement)
    if (element != null) {
      this.saveAsPDF(element)
    }
  }

  createPdfFileTitle(): string {
    this.getTranslation(this._title)
    return this.checkIfTranslationExists() ? `${this.titleTranslated}_${moment().format('DD-MM-YYYY')}.pdf` : `${moment().format('DD-MM-YYYY')}.pdf`
  }

  createExtraInfo(): string | string[] {
    let value: string | null = null
    if (Array.isArray(this._years)) {
      value = 'Years selected'
    }
    else if (this._years !== null) {
      value = 'Year'
    } else if (this._datesRange !== null) {
      value = 'SENSORS.AT'
    }
    else {
      return value = ''
    }

    this.getTranslation(value)
    if (this._years != null) return `${this.titleTranslated} ${this._years}`
    if (this._datesRange != null) return `${this.titleTranslated} ${this._datesRange}`
  }

  killIframe() {
    let iframe = document.getElementById('pdf-iframe');
    iframe.parentNode.removeChild(iframe);
  }

  saveAsPDF(element) {
    html2canvas(element, { scale: 1 })
      .then((canvas) => {
        const HTML_Width = document.getElementById(this._htmlElement).clientWidth
        const HTML_Height = document.getElementById(this._htmlElement).clientHeight

        const top_left_margin = 15;
        const PDF_Width = HTML_Width + (top_left_margin * 2);
        const PDF_Height = (PDF_Width * 1.5) + (top_left_margin * 2);

        const totalPDFPages = Math.ceil(HTML_Height / PDF_Height) - 1;

        const ctx = canvas.getContext('2d');

        const a4w = 190, a4h = 257//A4 size, 210mm x 297mm, 10 mm margin on each side, display area 190x277
        const imgHeight = Math.floor(a4h * canvas.width / a4w)//Convert pixel height of one page image to A4 display scale
        var renderedHeight = 0;

        const logo = document.getElementById("logo") as HTMLImageElement;//Icon placed in header

        const imgData = canvas.toDataURL("image/jpeg", 1.0);
        let pdf = new jsPDF('p', 'pt', 'a4');

        // Center the image
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const widthRatio = pageWidth / canvas.width;
        const heightRatio = pageHeight / canvas.height;
        const ratio = widthRatio > heightRatio ? heightRatio : widthRatio;

        const canvas_image_width = HTML_Width * ratio;
        const canvas_image_height = HTML_Height * ratio;

        let grFont = font.font;
        pdf.addFileToVFS("Arialbd.ttf", grFont);
        pdf.addFont("Arialbd.ttf", "Arialbd", "normal");
        pdf.setFont('Arialbd', 'normal');

        pdf.setFontSize(10)
        pdf.text(this.createPdfFileTitle(), 20, 70)

        pdf.setLineWidth(0.5);
        pdf.line(20, 73, pdf.getTextWidth(this.createPdfFileTitle()), 73);
        pdf.setDrawColor(255, 0, 0);

        pdf.setLineWidth(0.5);
        pdf.line(0, 95, pageWidth, 95);

        pdf.setFontSize(15)
        pdf.text(this.createExtraInfo(), 20, 92)

        pdf.setFillColor(154, 223, 251);
        pdf.roundedRect(0, 0, PDF_Width, 45, 4, 4, 'F');
        pdf.roundedRect(0, PDF_Height - top_left_margin, PDF_Width, top_left_margin, 4, 4, 'F');
        // pdf.rect(0, 0, PDF_Width, 64, 'F');
        // pdf.rect(0, PDF_Height - top_left_margin, PDF_Width, top_left_margin, 'F');
        pdf.addImage(imgData, 'JPG', top_left_margin, top_left_margin + 84, canvas_image_width - (top_left_margin * 2), canvas_image_height);
        pdf.addImage(logo, 'PNG', (canvas_image_width / 2) - 55, 10, 110, 30);

        for (var i = 1; i <= totalPDFPages; i++) {
          pdf.addPage('a4', 'p');

          pdf.setFontSize(10)
          pdf.text(this.createPdfFileTitle(), 20, 70)

          pdf.setLineWidth(0.5);
          pdf.line(20, 73, pdf.getTextWidth(this.createPdfFileTitle()), 73);
          pdf.setDrawColor(255, 0, 0);

          pdf.setLineWidth(0.5);
          pdf.line(0, 95, pageWidth, 95);

          pdf.setFontSize(15)
          pdf.text(this.createExtraInfo(), 20, 92)


          pdf.setFillColor(154, 223, 251);
          pdf.roundedRect(0, 0, PDF_Width, 45, 4, 4, 'F');
          pdf.roundedRect(0, PDF_Height - top_left_margin, PDF_Width, top_left_margin, 4, 4, 'F');
          // pdf.rect(0, 0, PDF_Width, 64, 'F');
          // pdf.rect(0, PDF_Height - top_left_margin, PDF_Width, top_left_margin, 'F');
          pdf.addImage(imgData, 'JPG', 0, -(PDF_Height * i) + (top_left_margin * (2 * i + 1) + 84), canvas_image_width, canvas_image_height);

          var page = document.createElement("canvas");
          page.width = canvas.width;
          page.height = Math.min(imgHeight, canvas.height - renderedHeight);//Maybe less than one page

          //Trim the specified area with getImageData and draw it into the canvas object created earlier
          page.getContext('2d').putImageData(ctx.getImageData(0, renderedHeight, canvas.width, Math.min(imgHeight, canvas.height - renderedHeight)), 0, 0);
          //Add an image to the page with a 10 mm / 20 mm margin
          pdf.addImage(page.toDataURL('image/jpeg', 1.0), 'JPEG', 10, 20, a4w, Math.min(a4h, a4w * page.height / page.width));
          //Add header logo
          pdf.addImage(logo, 'PNG', 5, 3, canvas_image_width, canvas_image_height);
        }

        if (this._saveDialog) pdf.save(this.createPdfFileTitle());

        this.loading = false;
        this.cdr.detectChanges()
        this.pdfExporterService.loadTheSpinner(this.loading)

        //Generate BLOB object
        var blob = pdf.output("blob");

        //Getting URL of blob object
        var blobURL = URL.createObjectURL(blob);


        if (this._openTab) {
          var tabWindowId = window.open('about:blank', '_blank');
          tabWindowId.location.href = blobURL;
        }

        if (this._showIframe) {
          //Showing PDF generated in iFrame element
          var iframe = document.getElementById('pdf-iframe') as HTMLIFrameElement;
          iframe.style.display = 'block';
          iframe.src = blobURL;

          setTimeout(() => {
            let iframe = document.getElementById('pdf-iframe');
            iframe.parentNode.removeChild(iframe);
          }, 10000)
        }

      })
  }
}
